import React, { useState } from 'react';
import { Box, IconButton, Tooltip, CircularProgress } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import StructuredSearchField from './StructuredSearchField';
import AdvancedSearchPanel from './AdvancedSearchPanel';

const WorkspaceToolbar = ({
  toolbar,
  fieldMetadata,
  advancedSearchFieldMeta,
  searchRows,
  navParams,
  presetSearch,
  onSearch,
  onSearchClear,
  onAdvancedSearch,
  onAdvancedSearchClear,
  onRefresh,
  onExport,
  loading,
  colors,
}) => {
  const [exporting, setExporting] = useState(false);

  // Universal "download this grid's data" action -- every grid gets it for
  // free, no per-view opt-in flag needed, as long as the caller passes
  // onExport (MetadataWorkspace always does for grid views with a loadScript).
  // Still renders even when there's no toolbar config at all, since the
  // button doesn't depend on any toolbar.* flag.
  const handleExportClick = async () => {
    if (!onExport || exporting) return;
    setExporting(true);
    try {
      await onExport();
    } finally {
      setExporting(false);
    }
  };

  if (!toolbar && !onExport) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 0.75,
        borderBottom: `1px solid ${colors.border}`,
        bgcolor: colors.surface,
        position: 'relative',
      }}
    >
      {toolbar?.showSearch && (
        <StructuredSearchField
          fieldMetadata={fieldMetadata}
          onSearch={onSearch}
          onClear={onSearchClear}
          presetText={presetSearch}
          colors={colors}
          placeholder={toolbar.searchPlaceholder || 'Search...'}
          externalParams={navParams}
        />
      )}
      <Box sx={{ flex: 1 }} />
      {toolbar?.showAdvancedSearch && (
        <AdvancedSearchPanel
          fieldMetadata={advancedSearchFieldMeta}
          rows={searchRows}
          onSearch={onAdvancedSearch}
          onClear={onAdvancedSearchClear}
          colors={colors}
        />
      )}
      {onExport && (
        <Tooltip title="Download data (CSV)">
          <span>
            <IconButton
              size="small"
              onClick={handleExportClick}
              disabled={exporting}
              sx={{ color: colors.textSecondary, '&:hover': { color: '#22c55e' } }}
            >
              {exporting ? <CircularProgress size={16} /> : <DownloadIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      )}
      {toolbar?.showRefresh && (
        <Tooltip title="Refresh">
          <span>
            <IconButton
              size="small"
              aria-label="Refresh"
              onClick={onRefresh}
              disabled={loading}
              sx={{ color: colors.textSecondary, '&:hover': { color: '#22c55e' } }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );
};

export default WorkspaceToolbar;
